'use client'

import * as React from 'react'
import { Separator } from "@sarkiai/ui"
import { useTranslations } from "@/components/I18nProvider"
import { GlassBox } from "@/components/custom/box"

export default function AboutPage() {
  const t = useTranslations("about")

  return (
    <main className="flex min-h-screen flex-col bg-background">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-black text-white overflow-hidden px-4 lg:px-12">
        <div className="absolute inset-0 bg-white/5 opacity-50" />
        <div className="container relative z-10 mx-auto">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h1 className="text-4xl md:text-8xl  tracking-tighter uppercase leading-none">
              {t('welcome')}
            </h1>
            <p className="text-xl md:text-2xl opacity-70 max-w-2xl mx-auto leading-relaxed">
              {t('subtitle')}
            </p>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />
      </section>

      <section className="py-20 md:py-32 px-4 lg:px-12">
        <div className="container mx-auto max-w-5xl">
          <GlassBox className="p-10 md:p-20 space-y-20 rounded-4xl border-black/5 dark:border-white/10 shadow-2xl shadow-black/5 bg-white/50 dark:bg-card/50 backdrop-blur-2xl">
            <div className="space-y-6">
              <p className="text-xl md:text-2xl font-medium leading-relaxed text-foreground/80 first-letter:text-5xl first-letter: first-letter:mr-3 first-letter:float-left">
                {t('intro')}
              </p>
            </div>

            <Separator className="bg-black/5 dark:bg-white/10 h-[1px]" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
              <section className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-black dark:bg-white text-white dark:text-black">
                  <span className="text-[10px]  uppercase tracking-widest">Aspiration</span>
                </div>
                <h2 className="text-3xl  uppercase tracking-tight leading-none">{t('mission.title')}</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {t('mission.content')}
                </p>
              </section>

              <section className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-black/5 dark:bg-white/10 text-black dark:text-white">
                  <span className="text-[10px]  uppercase tracking-widest">Innovation</span>
                </div>
                <h2 className="text-3xl  uppercase tracking-tight leading-none">{t('innovation.title')}</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {t('innovation.content')}
                </p>
              </section>
            </div>

            <section className="bg-black text-white rounded-[2.5rem] p-10 md:p-16 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-[80px] group-hover:scale-150 transition-transform duration-1000" />
              <div className="relative z-10 space-y-12">
                <h2 className="text-3xl md:text-5xl  uppercase tracking-tighter">{t('why_choose.title')}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-12">
                  <div className="flex items-start gap-6 group/item">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 group-hover/item:bg-white group-hover/item:text-black transition-colors">
                      <span className="">01</span>
                    </div>
                    <div>
                      <h3 className=" uppercase tracking-wide mb-2">{t('why_choose.eco_title')}</h3>
                      <p className="text-sm opacity-60 leading-relaxed">{t('why_choose.eco_content')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-6 group/item">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 group-hover/item:bg-white group-hover/item:text-black transition-colors">
                      <span className="">02</span>
                    </div>
                    <div>
                      <h3 className=" uppercase tracking-wide mb-2">{t('why_choose.modern_title')}</h3>
                      <p className="text-sm opacity-60 leading-relaxed">{t('why_choose.modern_content')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-6 group/item">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 group-hover/item:bg-white group-hover/item:text-black transition-colors">
                      <span className="">03</span>
                    </div>
                    <div>
                      <h3 className=" uppercase tracking-wide mb-2">{t('why_choose.secure_title')}</h3>
                      <p className="text-sm opacity-60 leading-relaxed">{t('why_choose.secure_content')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-6 group/item">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 group-hover/item:bg-white group-hover/item:text-black transition-colors">
                      <span className="">04</span>
                    </div>
                    <div>
                      <h3 className=" uppercase tracking-wide mb-2">{t('why_choose.custom_title')}</h3>
                      <p className="text-sm opacity-60 leading-relaxed">{t('why_choose.custom_content')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
              <section className="space-y-6">
                <h2 className="text-2xl  uppercase tracking-tight">{t('commitment.title')}</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {t('commitment.content')}
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl  uppercase tracking-tight">{t('join.title')}</h2>
                <div className="space-y-4">
                  <p className="text-muted-foreground leading-relaxed">
                    {t('join.content')}
                  </p>
                  <p className="font-bold text-foreground">
                    {t('join.cta')}
                  </p>
                </div>
              </section>
            </div>
          </GlassBox>
        </div>
      </section>
    </main>
  )
}
